import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * The model picker — which model each LLM surface uses (日本-325).
 *
 * **Everything still goes through OpenRouter**; what changed is that the model is no longer an
 * environment variable set at deploy time. Translate and Ask choose separately, from a catalogue
 * the server fetches live, so comparing two candidates on real sentences is a dropdown rather than
 * a redeploy.
 */

/**
 * One model as the picker draws it.
 *
 * Prices are **per million tokens** and separately nullable: `null` is "the catalogue did not quote
 * one", which is not the same as free — `free` is its own flag. Drawn as `—`, never as `0`.
 */
export interface ModelCard {
  id: string;
  name: string;
  contextLength: number;
  promptPerM: number | null;
  completionPerM: number | null;
  cacheReadPerM: number | null;
  /** Required for Ask. A model without it answers fluently and never calls a tool. */
  supportsTools: boolean;
  /** Whether the catalogue quotes a cache price at all — not *which* caching mechanism. */
  caches: boolean;
  /** A `~vendor/model-latest` alias: what it resolves to can change under you. */
  floating: boolean;
  free: boolean;
  expiresAt: string | null;
}

/**
 * What a surface uses, and whether that is a choice or a default.
 *
 * `card` is null for a slug the catalogue has never heard of — a hand-typed one, or *any* slug
 * while the catalogue is unreachable. The panel says so rather than implying the model is invalid,
 * because those two causes are indistinguishable from here and only one is a mistake.
 */
export interface ModelSelection {
  surface: string;
  selected: string;
  defaultModel: string;
  isDefault: boolean;
  card: ModelCard | null;
}

export interface ModelCatalog {
  surface: string;
  selection: ModelSelection;
  models: ModelCard[];
  /** Whether this deployment has an API key at all. Choosing a model and configuring a credential
   * are separate acts, and the panel says which is missing. */
  configured: boolean;
  /** A catalogue that could not be fetched. Not a failed request — `models` then holds the last
   * good list, or nothing on a cold start — so it is drawn beside the data, not instead of it. */
  error: string | null;
}

async function json<T>(path: string, init?: RequestInit, what = "load models"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ${what} (${res.status})`);
  }
  return res.json();
}

export function listModels(surface: string): Promise<ModelCatalog> {
  return json<ModelCatalog>(`/api/models?surface=${surface}`);
}

/** An empty slug returns the surface to its deployment default — the only way to say "whatever
 * this deployment ships with". */
export function chooseModel(surface: string, selected: string): Promise<ModelSelection> {
  return json<ModelSelection>(
    `/api/models/selection/${surface}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ selected }),
    },
    "choose the model",
  );
}

/** Re-fetch now rather than waiting out the server's six-hour cache. Without it a model released
 * this morning is missing from the dropdown for most of a day, and "not there yet" looks exactly
 * like "does not exist". */
export function refreshModels(surface: string): Promise<ModelCatalog> {
  return json<ModelCatalog>(
    `/api/models/refresh?surface=${surface}`,
    { method: "POST" },
    "refresh the model list",
  );
}

/** `$0.25` / `$12` / `—`. Trailing zeros dropped, because a price column of `$1.50` and `$12.00`
 * reads as more precision than the number carries. */
export function price(perMillion: number | null): string {
  if (perMillion === null) return "—";
  if (perMillion === 0) return "free";
  const fixed = perMillion < 1 ? perMillion.toFixed(3) : perMillion.toFixed(2);
  return `$${fixed.replace(/\.?0+$/, "")}`;
}

/** `1M` / `200K`. The context window as every model card writes it. */
export function contextLabel(tokens: number): string {
  if (tokens >= 1_000_000) return `${Math.round(tokens / 100_000) / 10}M`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}K`;
  return String(tokens);
}

/** The vendor half of a slug, for grouping: `anthropic/claude-sonnet-5` → `anthropic`. Leading `~`
 * stripped so floating aliases group with the vendor they track rather than under a tilde. */
export function vendorOf(modelId: string): string {
  const [vendor] = modelId.replace(/^~/, "").split("/");
  return vendor || "other";
}

/** Models grouped by vendor, vendors alphabetical, models by name within each.
 *
 * Pure so it can be tested without a DOM — `libraryRows.ts`'s rule, and the reason the panel holds
 * no sorting logic of its own.
 */
export function groupByVendor(models: ModelCard[]): [string, ModelCard[]][] {
  const groups = new Map<string, ModelCard[]>();
  for (const model of models) {
    const vendor = vendorOf(model.id);
    const bucket = groups.get(vendor);
    if (bucket) bucket.push(model);
    else groups.set(vendor, [model]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([vendor, entries]) =>
        [vendor, [...entries].sort((a, b) => a.name.localeCompare(b.name))] as [
          string,
          ModelCard[],
        ],
    );
}

/** Case-insensitive match on the slug or the display name. */
export function matches(model: ModelCard, filter: string): boolean {
  const needle = filter.trim().toLowerCase();
  if (!needle) return true;
  return (
    model.id.toLowerCase().includes(needle) || model.name.toLowerCase().includes(needle)
  );
}
